import { useState } from 'react'
import { ArrowRightLeft } from 'lucide-react'
import { Chip } from '@/components/ui'
import type { IntakeLine } from '../api/types'
import { lineMatchChip } from '../utils/line-match-chip'
import { ProductReassignDrawer } from './ProductReassignDrawer'

export interface SalesIntakeLineGridProps {
  lines: IntakeLine[]
  /** Disabled when the intake is in a non-editable state. */
  disabled?: boolean
  onLineEdit: (
    lineId: number,
    edits: {
      final_qty?: number
      final_unit_price?: number
      matched_product_id?: number
      matched_product_name?: string
    },
  ) => void
}

/**
 * Per-line review grid for the intake detail drawer. Editable cells:
 *   - final_qty  (number input, defaults to parsed_qty)
 *   - final_unit_price  (number input, defaults to parsed_unit_price)
 *   - matched product  (opens ProductReassignDrawer on click)
 *
 * v1 keeps it deliberately simple — uses inline number <input> rather
 * than the EditableGrid primitive (no inline keyboard navigation,
 * dirty-cell tracking, etc.). When real-world usage shows the
 * line count climbing past ~10 per intake, swap to EditableGrid for
 * better keyboard ergonomics.
 */
export function SalesIntakeLineGrid({
  lines,
  disabled = false,
  onLineEdit,
}: SalesIntakeLineGridProps) {
  const [reassignTarget, setReassignTarget] = useState<IntakeLine | null>(null)

  return (
    <>
      <div className="overflow-x-auto rounded-sm border border-border-subtle bg-surface">
        <table className="min-w-full text-sm">
          <thead className="bg-surface-sunk text-role-table-header">
            <tr>
              <th className="px-2 py-2 text-left">#</th>
              <th className="px-2 py-2 text-left">Customer Part #</th>
              <th className="px-2 py-2 text-left">Description</th>
              <th className="px-2 py-2 text-left">SPI Product</th>
              <th className="px-2 py-2 text-right">Qty</th>
              <th className="px-2 py-2 text-right">Unit Price</th>
              <th className="px-2 py-2 text-left">Match</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line) => (
              <Row
                key={line.id}
                line={line}
                disabled={disabled}
                onLineEdit={onLineEdit}
                onReassignClick={() => setReassignTarget(line)}
              />
            ))}
          </tbody>
        </table>
      </div>

      <ProductReassignDrawer
        open={reassignTarget != null}
        onOpenChange={(o) => {
          if (!o) setReassignTarget(null)
        }}
        parsedPartNumber={reassignTarget?.parsed_part_number ?? null}
        parsedDescription={reassignTarget?.parsed_description ?? null}
        onConfirm={(product) => {
          if (reassignTarget) {
            onLineEdit(reassignTarget.id, {
              matched_product_id: product.id,
              matched_product_name: product.name,
            })
          }
          setReassignTarget(null)
        }}
      />
    </>
  )
}

function Row({
  line,
  disabled,
  onLineEdit,
  onReassignClick,
}: {
  line: IntakeLine
  disabled: boolean
  onLineEdit: SalesIntakeLineGridProps['onLineEdit']
  onReassignClick: () => void
}) {
  const qty = line.final_qty ?? line.parsed_qty ?? ''
  const price = line.final_unit_price ?? line.parsed_unit_price ?? ''
  const match = lineMatchChip(line)

  return (
    <tr className="border-t border-border-subtle text-role-table-cell">
      <td className="px-2 py-2 text-tertiary">{line.line_no}</td>
      <td className="px-2 py-2 text-secondary">{line.parsed_part_number ?? '—'}</td>
      <td className="px-2 py-2 text-secondary">
        <span className="block max-w-[24ch] truncate" title={line.parsed_description ?? ''}>
          {line.parsed_description ?? '—'}
        </span>
      </td>
      <td className="px-2 py-2">
        <button
          type="button"
          onClick={onReassignClick}
          disabled={disabled}
          title={line.matched_product_name ? 'Reassign product' : 'Pick SPI product'}
          aria-label={`${line.matched_product_name ? 'Reassign' : 'Pick'} product for line ${line.line_no}`}
          className="inline-flex items-center gap-1 rounded-xs px-1.5 py-0.5 text-left hover:bg-surface-sunk focus:outline-none focus-visible:ring-2 focus-visible:ring-border-focus disabled:cursor-not-allowed disabled:opacity-60"
        >
          {line.matched_product_name ? (
            <span className="text-role-data-secondary">{line.matched_product_name}</span>
          ) : (
            <em className="text-tertiary">unmatched</em>
          )}
          <ArrowRightLeft size={12} className="shrink-0 text-tertiary" aria-hidden />
        </button>
      </td>
      <td className="px-2 py-2 text-right">
        <input
          type="number"
          step="any"
          defaultValue={String(qty)}
          disabled={disabled}
          onBlur={(e) => {
            const v = parseFloat(e.target.value)
            if (!Number.isNaN(v) && v !== Number(qty)) {
              onLineEdit(line.id, { final_qty: v })
            }
          }}
          className="w-20 rounded-xs border border-border-default bg-surface px-1.5 py-1 text-right text-sm text-primary focus:border-border-focus focus:outline-none disabled:bg-surface-sunk disabled:text-text-disabled"
          aria-label={`Quantity for line ${line.line_no}`}
        />
      </td>
      <td className="px-2 py-2 text-right">
        <input
          type="number"
          step="any"
          defaultValue={String(price)}
          disabled={disabled}
          onBlur={(e) => {
            const v = parseFloat(e.target.value)
            if (!Number.isNaN(v) && v !== Number(price)) {
              onLineEdit(line.id, { final_unit_price: v })
            }
          }}
          className="w-24 rounded-xs border border-border-default bg-surface px-1.5 py-1 text-right text-sm text-primary focus:border-border-focus focus:outline-none disabled:bg-surface-sunk disabled:text-text-disabled"
          aria-label={`Unit price for line ${line.line_no}`}
        />
      </td>
      <td className="px-2 py-2">
        <Chip size="xs" variant={match.variant} dot>
          {match.label}
        </Chip>
      </td>
    </tr>
  )
}
